import { useEffect, useMemo, useRef, useState } from "react";
import type { Car } from "@vms/types";
import { NoContentNotice } from "./NoContentNotice.js";

/**
 * Per-item coverage info for cross-venue scopes. Same shape as
 * `TrackCoverageMap` but keyed on `carId`.
 */
export interface CarCoverageMap {
  totalVenues: number;
  byCarId: Record<string, { availableInVenues: string[] }>;
}

interface CarMultiSelectProps {
  cars: Car[] | null | undefined;
  /** Selected `carId` values. */
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
  coverage?: CarCoverageMap;
  placeholder?: string;
}

/**
 * Chip list + inline search-and-pick dropdown for cars. Multi-select with
 * one entry per `carId` (no skin selection — leaderboard filters use base
 * cars only). Mirrors the inline picker in hub-ui's leaderboard form.
 */
export function CarMultiSelect({
  cars,
  value,
  onChange,
  disabled,
  coverage,
  placeholder = "+ Add car filter...",
}: CarMultiSelectProps) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch("");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const filtered = useMemo(() => {
    if (!cars) return [];
    const q = search.trim().toLowerCase();
    return cars
      .filter((c) => !value.includes(c.carId))
      .filter((c) => {
        if (!q) return true;
        return (
          c.name.toLowerCase().includes(q) ||
          (c.brand ?? "").toLowerCase().includes(q)
        );
      });
  }, [cars, value, search]);

  function add(carId: string) {
    if (!value.includes(carId)) {
      onChange([...value, carId]);
    }
    setSearch("");
  }

  function remove(carId: string) {
    onChange(value.filter((x) => x !== carId));
  }

  function coverageHintFor(carId: string): string | null {
    if (!coverage) return null;
    const entry = coverage.byCarId[carId];
    if (!entry) return null;
    if (entry.availableInVenues.length >= coverage.totalVenues) return null;
    return `In ${entry.availableInVenues.length} of ${coverage.totalVenues} venues`;
  }

  // Loaded (an array) but empty — nothing to filter by. `null`/`undefined`
  // means still loading, so we don't flash the notice prematurely.
  const noCarsUploaded = Array.isArray(cars) && cars.length === 0;

  return (
    <div>
      <div className="space-y-2 mb-2">
        {value.map((carId) => {
          const car = cars?.find((c) => c.carId === carId);
          const firstPreview = car?.skins?.find((s) => s.previewUrl)?.previewUrl;
          const hint = coverageHintFor(carId);
          return (
            <div
              key={carId}
              className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 bg-white"
            >
              {firstPreview ? (
                <div className="w-12 h-9 bg-gray-900 rounded flex-shrink-0 flex items-center justify-center">
                  <img src={firstPreview} alt="" className="max-w-full max-h-full object-contain rounded" />
                </div>
              ) : car?.badgeUrl ? (
                <img src={car.badgeUrl} alt="" className="w-9 h-9 object-contain flex-shrink-0" />
              ) : null}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{car?.name ?? carId}</p>
                {car?.brand && <p className="text-xs text-gray-500">{car.brand}</p>}
                {hint && <p className="text-xs text-amber-600 mt-0.5">{hint}</p>}
              </div>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => remove(carId)}
                  className="text-gray-400 hover:text-red-500 flex-shrink-0"
                  aria-label="Remove car"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          );
        })}
      </div>
      {noCarsUploaded ? (
        <NoContentNotice message="No cars uploaded yet. Upload car content to choose one." />
      ) : !disabled ? (
        <div ref={ref}>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setOpen(true)}
            placeholder={placeholder}
            className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
          />
          {open && cars && (
            <div className="mt-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg bg-white">
              {filtered.length === 0 ? (
                <p className="px-3 py-3 text-sm text-gray-400 text-center">
                  {search ? "No matching cars." : "All cars already added."}
                </p>
              ) : (
                filtered.map((car) => {
                  const firstPreview = car.skins?.find((s) => s.previewUrl)?.previewUrl;
                  const hint = coverageHintFor(car.carId);
                  return (
                    <button
                      key={car.id}
                      type="button"
                      onClick={() => add(car.carId)}
                      className="w-full text-left flex items-center gap-3 p-3 hover:bg-gray-50 border-b border-gray-100 last:border-0"
                    >
                      {firstPreview ? (
                        <div className="w-12 h-9 bg-gray-900 rounded flex-shrink-0 flex items-center justify-center">
                          <img src={firstPreview} alt="" className="max-w-full max-h-full object-contain rounded" />
                        </div>
                      ) : car.badgeUrl ? (
                        <img src={car.badgeUrl} alt="" className="w-9 h-9 object-contain flex-shrink-0" />
                      ) : null}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{car.name}</p>
                        {car.brand && <p className="text-xs text-gray-500">{car.brand}</p>}
                        {hint && <p className="text-xs text-amber-600 mt-0.5">{hint}</p>}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
